import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { GripHorizontal } from "lucide-react";

const maximumHeight = 2400;
const clampHeight = (height: number, minimumHeight: number) => Math.round(Math.min(maximumHeight, Math.max(minimumHeight, height)));

type Props = {
  title: string;
  children: ReactNode;
  scrollAreaSelector?: string;
  minimumHeight?: number;
  resizeLabel?: string;
};

/** Resize the existing scroll container without remounting its rows or changing its data. */
export function ResizableFinanceTable({ title, children, scrollAreaSelector = ".pnl-table-wrap", minimumHeight = 240, resizeLabel = `Resize ${title} table` }: Props) {
  const [height, setHeight] = useState<number | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const [dragging, setDragging] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; y: number; height: number } | null>(null);
  const id = useId();
  const scrollArea = () => wrapper.current?.querySelector<HTMLElement>(scrollAreaSelector);
  useEffect(() => {
    if (!dragging) return;
    // Keep the page from following the moving handle through scroll anchoring.
    const root = document.documentElement;
    const previous = root.style.overflowAnchor;
    root.style.overflowAnchor = "none";
    return () => { root.style.overflowAnchor = previous; };
  }, [dragging]);
  useEffect(() => {
    const area = scrollArea();
    if (!area) return;
    const observer = new ResizeObserver(() => setMeasuredHeight(Math.round(area.getBoundingClientRect().height)));
    observer.observe(area);
    return () => observer.disconnect();
  }, [scrollAreaSelector]);
  return <div ref={wrapper} className="fin-resizable-table" data-resized={height !== null || undefined}
    style={height === null ? undefined : { "--fin-table-height": `${height}px` } as CSSProperties}>
    <div id={id} className="fin-resizable-content">{children}</div>
    <div className="fin-table-resize-handle" role="separator" tabIndex={0} aria-label={resizeLabel} aria-orientation="horizontal"
      aria-controls={id} aria-valuemin={minimumHeight} aria-valuemax={maximumHeight} aria-valuenow={measuredHeight || minimumHeight}
      aria-valuetext={`${measuredHeight} pixels tall`} title="Drag up or down to resize. Use the arrow keys when focused."
      data-dragging={dragging || undefined}
      onPointerDown={event => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus({ preventScroll: true });
        drag.current = { pointerId: event.pointerId, y: event.clientY, height: scrollArea()?.getBoundingClientRect().height ?? minimumHeight };
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
      }}
      onPointerMove={event => {
        const start = drag.current;
        if (start?.pointerId === event.pointerId) setHeight(clampHeight(start.height + event.clientY - start.y, minimumHeight));
      }}
      onPointerUp={event => {
        if (drag.current?.pointerId !== event.pointerId) return;
        drag.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
      }}
      onLostPointerCapture={() => { drag.current = null; setDragging(false); }}
      onKeyDown={event => {
        const current = scrollArea()?.getBoundingClientRect().height ?? minimumHeight;
        const next = event.key === "ArrowDown" ? current + 40 : event.key === "ArrowUp" ? current - 40
          : event.key === "Home" ? minimumHeight : event.key === "End" ? maximumHeight : null;
        if (next === null) return;
        event.preventDefault();
        setHeight(clampHeight(next, minimumHeight));
      }}>
      <GripHorizontal size={18} aria-hidden="true" /><span>Drag to resize</span>
    </div>
  </div>;
}
